/**
 * DIE ZENTRALEN KALKULATIONSPARAMETER.
 *
 * WARUM ES DIESEN BEREICH GIBT
 *
 * Wechselkurs, Frachtsaetze und Abgabensaetze galten schon immer fuer das
 * ganze Haus -- aber es gab keine Stelle, an der man sie sehen oder aendern
 * konnte. Sie standen in Migrationen. Wer den Frachtsatz anheben wollte,
 * brauchte einen Entwickler; wer wissen wollte, mit welchem Kurs gerechnet
 * wird, musste eine Produktkalkulation oeffnen und dort nachsehen.
 *
 * KEINE PRODUKTKARTE UND KEIN ARTIKELKONTEXT
 *
 * Diese Werte sind keine Produkteigenschaften. An jedem Artikel gepflegt
 * waeren sie dreizehnhundertmal dieselbe Zahl -- und beim naechsten
 * Satzwechsel dreizehnhundert Stellen, von denen eine vergessen wird.
 *
 * VIER KARTEN, WEIL ES VIER FRAGEN SIND
 *
 *   Waehrung      zu welchem Kurs rechnen wir?
 *   Fracht        was kostet der Transport, je Transportart?
 *   VerpackungsG  was kostet die Verpackung als Abgabe?
 *   ElektroG      was kostet das Produktgewicht als Abgabe?
 *
 * Eine einzige Liste aus dreizehn Zeilen waere kuerzer und unbrauchbar: der
 * Sicherheitsabschlag gehoert neben den Kurs und nicht zwischen Provision und
 * Rundungsschritt.
 *
 * ALTE STAENDE BLEIBEN STEHEN
 *
 * Ein Parametersatz, mit dem schon gerechnet wurde, ist unveraenderlich
 * (Migration 0017, `parameter_set_locked`). Eine Aenderung legt deshalb einen
 * NEUEN Satz mit eigenem Gueltigkeitsdatum an -- und dieses Datum gibt ein
 * Mensch ein. Es zu raten hiesse, eine Aussage ueber den Zeitpunkt zu
 * treffen, ab dem das Haus anders kalkuliert.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { CalculationParametersResponse, CalculationParameterSet } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { heuteLokal } from "../lib/datum.ts";
import { useI18n, type I18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { ErrorState, LoadingState, Unknown } from "./States.tsx";
import "./CalculationParameters.css";

/*
 * WELCHER PARAMETER IN WELCHE KARTE GEHOERT -- und in welcher Einheit er
 * gepflegt wird.
 *
 * `faktor` ist die Zahl, mit der der gespeicherte Wert multipliziert wird, um
 * die Pflegeeinheit zu ergeben. Die Abgabensaetze stehen in EUR je Kilogramm,
 * genannt und verhandelt werden sie aber in EUR je TONNE -- 237,00 liest sich
 * als Preis, 0,237 als Rundungsfehler. Gerechnet wird weiter in Kilogramm;
 * umgerechnet wird nur an dieser einen Stelle, beim Lesen und beim Schreiben.
 */
interface Feld {
  readonly code: string;
  /** Einheit, in der gepflegt und angezeigt wird. */
  readonly einheit: string;
  /** Gespeicherter Wert x faktor = angezeigter Wert. */
  readonly faktor: number;
  /** Nachkommastellen der Anzeige. */
  readonly stellen: number;
}
interface Karte {
  readonly code: string;
  readonly felder: readonly Feld[];
}

const KARTEN: readonly Karte[] = [
  { code: "waehrung", felder: [
    { code: "usd_eur_rate", einheit: "EUR/USD", faktor: 1, stellen: 2 },
    { code: "usd_eur_safety_discount", einheit: "EUR/USD", faktor: 1, stellen: 2 },
  ] },
  { code: "fracht", felder: [
    { code: "sea_freight_usd_per_cbm", einheit: "USD/cbm", faktor: 1, stellen: 2 },
    { code: "rail_freight_usd_per_cbm", einheit: "USD/cbm", faktor: 1, stellen: 2 },
    { code: "air_freight_usd_per_kg", einheit: "USD/kg", faktor: 1, stellen: 2 },
    /* Der Divisor gehoert zur Luftfracht und zu nichts sonst (0058). */
    { code: "air_volumetric_divisor_cm3_per_kg", einheit: "cm³/kg", faktor: 1, stellen: 0 },
  ] },
  { code: "verpackungsg", felder: [
    { code: "packaging_act_cardboard_eur_per_kg", einheit: "EUR/t", faktor: 1000, stellen: 2 },
    { code: "packaging_act_plastic_eur_per_kg", einheit: "EUR/t", faktor: 1000, stellen: 2 },
  ] },
  { code: "elektrog", felder: [
    { code: "electro_act_eur_per_kg", einheit: "EUR/kg", faktor: 1, stellen: 2 },
  ] },
];

/* Die Rechen-Einheit der Abgabensaetze. Gepflegt wird in EUR je Tonne,
   gerechnet in EUR je Kilogramm -- der zweite Wert steht leise daneben. */
const EUR_JE_KG = "EUR/kg";

const zahl = (raw: string, lang: string, stellen: number): string => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toLocaleString(lang === "en" ? "en-GB" : "de-DE",
    { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
};

export function CalculationParameters({ mayWrite }: { mayWrite: boolean }): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [data, setData] = useState<CalculationParametersResponse | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bearbeitet, setBearbeitet] = useState(false);
  const [entwurf, setEntwurf] = useState<Record<string, string>>({});
  const [gueltigAb, setGueltigAb] = useState("");
  const [speichert, setSpeichert] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [speicherfehler, setSpeicherfehler] = useState<string | null>(null);
  /* Sperre gegen den zweiten Klick eines Doppelklicks -- vor dem naechsten Render. */
  const laeuft = useRef(false);

  const laden = useCallback(async (): Promise<void> => {
    try { setData(await api.calculationParameters()); setFehler(null); }
    catch (e) { setFehler(errorText(i18n, e)); }
  }, [i18n]);
  useEffect(() => { void laden(); }, [laden]);

  if (fehler !== null) return <ErrorState message={fehler} onRetry={() => void laden()} />;
  if (data === null) return <LoadingState />;

  /*
   * DER GELTENDE STAND ist der juengste nicht abgeloeste -- dieselbe Auswahl,
   * die der Fachkern trifft. Staende mit spaeterem Datum sind vorbereitet und
   * gelten noch nicht; sie stehen in der Historie und nicht oben.
   */
  const heute = heuteLokal();
  /*
   * DIESELBE REGEL WIE IM FACHKERN (0059): die juengste Revision des
   * juengsten bereits gueltigen, nicht abgeloesten Datums -- fuer „heute"
   * die geltende, fuer ein gewaehltes Datum die GRUNDLAGE der neuen Revision.
   */
  const satzAm = (stichtag: string): CalculationParameterSet | null =>
    data.sets.filter((s) => !s.superseded && s.validFrom <= stichtag)
      .sort((a, b) => b.validFrom.localeCompare(a.validFrom) || b.revision - a.revision)[0] ?? null;
  const geltend = satzAm(heute);
  const basis = gueltigAb === "" ? geltend : satzAm(gueltigAb);

  const wert = (code: string): string | null =>
    geltend?.parameters.find((p) => p.code === code)?.value ?? null;
  const basiswert = (code: string): string | null =>
    basis?.parameters.find((p) => p.code === code)?.value ?? null;

  /* Referenzkurs minus Abschlag. Dieselbe Regel wie im Fachkern -- hier nur
     angezeigt, damit niemand sie im Kopf nachvollziehen muss. */
  const referenz = wert("usd_eur_rate");
  const abschlag = wert("usd_eur_safety_discount");
  const kalkKurs = referenz !== null && abschlag !== null
    ? Number(referenz) - Number(abschlag) : null;

  const feldwert = (f: Feld): string => {
    if (entwurf[f.code] !== undefined) return entwurf[f.code]!;
    const w = basiswert(f.code);
    return w === null ? "" : String(Number(w) * f.faktor);
  };

  /*
   * DER ENTWURF IST LOKAL -- bis „Neuen Stand speichern".
   *
   * Eintippen, Fokuswechsel, Abbrechen, Neuladen und Verlassen der Seite
   * schreiben nichts: `entwurf` lebt nur in diesem Formular. Geschrieben
   * wird ausschliesslich in `speichern`.
   */
  const geaendert = KARTEN.flatMap((k) => k.felder).filter((f) => {
    const e = entwurf[f.code];
    if (e === undefined) return false;
    const w = basiswert(f.code);
    const alt = w === null ? "" : String(Number(w) * f.faktor);
    return e.trim() !== alt;
  });

  /*
   * WARUM NICHT GESPEICHERT WERDEN KANN -- sichtbar, nicht still.
   *
   * Bis hierher war „Neuen Stand anlegen" grau, solange kein Gueltig-ab-Datum
   * eingetragen war, und der Hinweis dazu erschien erst NACH einer Aenderung
   * und ohne zu sagen, was zu tun ist. Jetzt nennt die Seite den ersten
   * Grund, der das Speichern verhindert, und was der Benutzer tun muss.
   */
  const roh = (f: Feld): string => entwurf[f.code]!.trim().replace(",", ".");
  const ungueltig = geaendert.find((f) => {
    const r = roh(f);
    if (r === "") return true;               /* ein gepflegter Wert laesst sich nicht leeren */
    const n = Number(r);
    if (!Number.isFinite(n) || n < 0) return true;
    return f.code === "air_volumetric_divisor_cm3_per_kg" && !(n > 0);
  }) ?? null;
  /* Ein belegtes Datum ist seit 0059 kein Hindernis: es entsteht die naechste Revision. */
  const revisionen = gueltigAb === "" ? [] : data.sets.filter((x) => x.validFrom === gueltigAb).map((x) => x.revision);
  const letzteRevision = revisionen.length === 0 ? null : Math.max(...revisionen);
  const sperrgrund: string | null =
      geaendert.length === 0 ? t("param.why.noChange")
    : ungueltig !== null ? t("param.why.invalid", { field: t(`param.${ungueltig.code}` as never) })
    : gueltigAb === "" ? t("param.why.noDate")
    : null;

  async function speichern(): Promise<void> {
    if (sperrgrund !== null || speichert || laeuft.current) return;   /* kein Doppelspeichern */
    laeuft.current = true;
    setSpeichert(true); setMeldung(null);
    try {
      /*
       * EIN NEUER STAND, NICHT EIN UEBERSCHRIEBENER -- und in EINEM Zug.
       *
       * Alle Aenderungen gehen in denselben neuen Satz, und zwar atomar
       * (`POST /calculation-parameter-sets`). Frueher legte der erste Aufruf
       * den Satz an und die uebrigen trugen nach; scheiterte einer, blieb ein
       * halber Stand in der Historie.
       */
      const r = await api.createParameterSet({
        validFrom: gueltigAb,
        values: geaendert.map((f) => ({ parameterCode: f.code, value: Number(roh(f)) / f.faktor })),
      });
      setEntwurf({}); setGueltigAb(""); setBearbeitet(false);
      await laden();
      setMeldung(r.created
        ? t("param.savedRevision", { date: r.validFrom, revision: r.revision })
        : t("param.unchanged", { date: r.validFrom, revision: r.revision }));
    } catch (e) {
      /* Der Fehler gehoert in den Editor, nicht an die Stelle der ganzen Seite. */
      setMeldung(null); setSpeicherfehler(errorText(i18n, e));
    }
    finally { setSpeichert(false); laeuft.current = false; }
  }

  /* ✎: Bearbeiten beginnt mit dem heutigen Kalendertag als Gueltig-ab -- im State. */
  function beginnen(): void {
    setMeldung(null); setSpeicherfehler(null); setEntwurf({});
    setGueltigAb(heuteLokal());
    setBearbeitet(true);
  }
  /* Abbrechen verwirft alles Ungespeicherte, schreibt nichts. */
  function abbrechen(): void {
    setEntwurf({}); setGueltigAb(""); setSpeicherfehler(null); setBearbeitet(false);
  }

  return (
    <section className="param">
      <header className="param__kopf">
        <h2 className="param__titel">{t("param.title")}</h2>
        {geltend !== null && (
          <p className="param__stand">
            {t("param.validFromRevision", { date: geltend.validFrom, revision: geltend.revision,
              at: new Date(geltend.createdAt).toLocaleString(i18n.lang === "en" ? "en-GB" : "de-DE"),
              by: geltend.createdBy ?? "—" })}
            {geltend.note && <span className="param__note"> · {geltend.note}</span>}
          </p>
        )}
        {/* ✎ -- das HOMEBASE-Zeichen fuer „Bearbeiten", wie in Akte und Portal. */}
        {mayWrite && !bearbeitet && (
          <button type="button" className="subnav__pencil param__stift" title={t("param.edit")}
            aria-label={t("param.edit")} onClick={beginnen}>✎</button>
        )}
        {/*
          * IM BEARBEITEN STEHEN SPEICHERN UND ABBRECHEN DORT, WO DER STIFT
          * STAND (Gueltig-ab-Bug 001). Wer den Stift gefunden hat, findet an
          * derselben Stelle, wie die Bearbeitung endet -- nicht erst unter vier
          * Karten.
          */}
        {bearbeitet && (
          <span className="param__kopfaktionen">
            <button className="btn btn--primary param__speichern" disabled={speichert || sperrgrund !== null}
              aria-describedby="param-sperrgrund"
              onClick={() => void speichern()}>{t("param.save")}</button>
            <button className="btn param__abbrechen" disabled={speichert} onClick={abbrechen}>
              {t("param.cancel")}
            </button>
          </span>
        )}
      </header>

      {bearbeitet && (
        <div className="param__aktionen">
          {/*
            * DAS GUELTIG-AB STEHT IM STATE, NICHT NUR IM BILD.
            *
            * Bis hierher begann es leer. Chrome zeigte das als „tt.mm.jjjj",
            * Safari aber zeigt in einem LEEREN Datumsfeld das heutige Datum an
            * -- sichtbar „19.09.2026", im Formular nichts, und die Seite
            * meldete „Gültig-ab-Datum fehlt". Jetzt setzt ✎ den heutigen
            * Kalendertag (Zeitzone des Benutzers) wirklich als Wert; wer ab
            * einem anderen Tag rechnen will, aendert ihn hier.
            */}
          <label className="param__datum">
            {t("param.newValidFrom")}
            <input type="date" value={gueltigAb} disabled={speichert}
              onChange={(e) => { setGueltigAb(e.target.value); setSpeicherfehler(null); }} />
          </label>
          <span id="param-sperrgrund" className="param__warn" role="status">
            {sperrgrund ?? (letzteRevision !== null
              ? t("param.why.readyRevision", { date: gueltigAb, revision: letzteRevision, next: letzteRevision + 1 })
              : t("param.why.ready", { date: gueltigAb }))}
          </span>
          {speicherfehler !== null && (
            <span className="param__fehler" role="alert">{speicherfehler}</span>
          )}
        </div>
      )}

      {meldung !== null && <p className="param__meldung">{meldung}</p>}

      <div className="param__karten">
        {KARTEN.map((k) => (
          <article key={k.code} className="param__karte">
            <h3 className="param__kartentitel">{t(`param.card.${k.code}` as never)}</h3>
            {/* Im Pflegemodus fluchten Feld und Einheit in einer Spalte
                (§15) -- im Lesemodus bleibt die Zahlenspalte rechts. */}
            <dl className={bearbeitet ? "param__zeilen param__zeilen--pflege" : "param__zeilen"}>
              {k.felder.map((f) => (
                <div key={f.code} className="param__zeile">
                  <dt className="param__label">{t(`param.${f.code}` as never)}</dt>
                  <dd className="param__wert">
                    {bearbeitet
                      ? <>
                          <input className="param__feld" inputMode="decimal"
                            value={feldwert(f)} disabled={speichert}
                            aria-label={t(`param.${f.code}` as never)}
                            onChange={(ev) => setEntwurf((x) =>
                              ({ ...x, [f.code]: ev.target.value }))} />
                          <span className="param__einheit">{f.einheit}</span>
                        </>
                      : wert(f.code) === null
                        /* „nicht bestimmt" und nicht 0: fuer See- und
                           Luftfracht liegt kein bestaetigter Satz vor, und
                           eine 0 waere eine Kalkulation ohne Fracht, die wie
                           eine vollstaendige aussieht. */
                        ? <Unknown />
                        : <>
                            <strong>{zahl(String(Number(wert(f.code)!) * f.faktor),
                                           i18n.lang, f.stellen)}</strong>
                            <span className="param__einheit">{f.einheit}</span>
                            {/* Die Zweiteinheit steht leise daneben: gepflegt
                                wird in EUR je Tonne, gerechnet in EUR je
                                Kilogramm. Wer nur eine der beiden saehe,
                                muesste die andere im Kopf umrechnen. */}
                            {f.faktor !== 1 && (
                              <span className="param__zweit">
                                {zahl(wert(f.code)!, i18n.lang, 3)} {EUR_JE_KG}
                              </span>
                            )}
                          </>}
                  </dd>
                </div>
              ))}

              {/*
                * DER KALKULATIONSKURS -- berechnet, nicht gepflegt.
                *
                * Er traegt kein Eingabefeld, auch nicht im Bearbeitungsmodus.
                * Als drittes Feld waere er die Zahl, die den beiden anderen
                * widersprechen kann: wer den Referenzkurs korrigiert und ihn
                * vergisst, rechnet ab dann mit einem Abschlag, der nirgends
                * steht.
                */}
              {k.code === "waehrung" && (
                <div className="param__zeile param__zeile--berechnet">
                  <dt className="param__label">{t("param.usd_eur_calc_rate")}</dt>
                  <dd className="param__wert">
                    {kalkKurs === null
                      ? <Unknown />
                      : <><strong>{zahl(String(kalkKurs), i18n.lang, 2)}</strong>
                          {/* Dieselbe Einheit wie der Referenzkurs, aus
                              derselben Quelle -- eine zweite Schreibweise
                              waere die Stelle, an der sie auseinanderlaufen. */}
                          <span className="param__einheit">{k.felder[0]!.einheit}</span>
                          <span className="param__zweit">
                            {t("param.calcRateFrom", {
                              ref: zahl(referenz!, i18n.lang, 2),
                              ab: zahl(abschlag!, i18n.lang, 2) })}
                          </span></>}
                  </dd>
                </div>
              )}
              {k.code === "elektrog" && (
                <p className="param__hinweis">{t("param.electroActNote")}</p>
              )}
            </dl>
          </article>
        ))}
      </div>

      {/*
        * DIE HISTORIE steht unter den Karten und nicht in ihnen: sie
        * beantwortet eine andere Frage -- nicht „was gilt?", sondern „was galt
        * wann?". Ein alter Stand wird nie ueberschrieben; er bleibt lesbar,
        * weil jede freigegebene Kalkulation auf einem von ihnen beruht.
        */}
      <details className="param__historie">
        <summary>{t("param.history", { n: data.sets.length })}</summary>
        {/* Dieselbe Regel wie bei der Benutzertabelle (§34): die
            Historie hat sechs Spalten und scrollt bei Bedarf selbst. */}
        <div className="param__tabellewrap">
        <table className="param__tabelle">
          <thead>
            <tr>
              <th>{t("param.validFromShort")}</th>
              <th>{t("param.revision")}</th>
              <th>{t("param.savedAt")}</th>
              <th>{t("param.status")}</th>
              <th>{t("param.values")}</th>
            </tr>
          </thead>
          <tbody>
            {[...data.sets].sort((a, b) => b.validFrom.localeCompare(a.validFrom) || b.revision - a.revision).map((s) => (
              <tr key={s.id} className={s.id === geltend?.id ? "param__zeile--geltend" : undefined}>
                <td>{s.validFrom}</td>
                <td>{s.revision}</td>
                <td>{new Date(s.createdAt).toLocaleString(i18n.lang === "en" ? "en-GB" : "de-DE")} · {s.createdBy ?? "—"}</td>
                <td>
                  {s.id === geltend?.id ? t("param.current")
                    : s.superseded ? t("param.superseded")
                      /* Eine aeltere Revision desselben Datums ist korrigiert, nicht vergangen. */
                      : data.sets.some((x) => x.validFrom === s.validFrom && !x.superseded && x.revision > s.revision)
                        ? t("param.replacedByRevision", { revision: Math.max(...data.sets
                            .filter((x) => x.validFrom === s.validFrom).map((x) => x.revision)) })
                      : s.validFrom > heute ? t("param.future") : t("param.past")}
                  {s.inUse && <span className="param__note"> · {t("param.inUse")}</span>}
                </td>
                <td>{s.parameters.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </details>
    </section>
  );
}
